import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Breadcrumbs
} from '@mui/material';
import {
  Upload as UploadIcon,
  Folder as FolderIcon,
  Plus as AddIcon,
  FileText as EpicIcon,
  BookOpen as StoryIcon,
  CheckSquare as TaskIcon,
  Bug as BugIcon
} from 'lucide-react';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Label color options
const labelColors = [
  { name: 'Blue', value: '#0052cc' },
  { name: 'Green', value: '#00875a' },
  { name: 'Purple', value: '#8777d9' },
  { name: 'Red', value: '#de350b' },
  { name: 'Orange', value: '#ff7452' },
  { name: 'Yellow', value: '#ffab00' },
  { name: 'Teal', value: '#00a3bf' },
  { name: 'Pink', value: '#cd519d' }
];

// Hierarchical Document Selection Dialog
export const HierarchySelectionDialog = ({
  open,
  onClose,
  selectedHierarchy,
  issueData,
  createUploadPath,
  onFileUpload
}) => {
  return (
    <Dialog 
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Select Document Location in Project Hierarchy</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Current Selection Breadcrumb */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Current Selection:
            </Typography>
            <Breadcrumbs>
              <Chip 
                icon={<FolderIcon size={16} />}
                label={selectedHierarchy.project?.name || 'No Project'}
                color={selectedHierarchy.project ? 'primary' : 'default'}
              />
              {selectedHierarchy.epic && (
                <Chip 
                  icon={<EpicIcon size={16} />}
                  label={selectedHierarchy.epic.name}
                  color="secondary"
                />
              )}
              {selectedHierarchy.story && (
                <Chip 
                  icon={<StoryIcon size={16} />}
                  label={selectedHierarchy.story.name}
                  color="info"
                />
              )}
              {selectedHierarchy.task && (
                <Chip 
                  icon={selectedHierarchy.task.type === 'Bug' ? <BugIcon size={16} /> : <TaskIcon size={16} />}
                  label={selectedHierarchy.task.name}
                  color="warning"
                />
              )}
              {issueData.issueType === 1 && selectedHierarchy.task && (
                <Chip 
                  icon={<BugIcon size={16} />}
                  label="Bug"
                  color="error"
                />
              )}
            </Breadcrumbs>
          </Box>

          {/* Upload Path Display */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Files will be uploaded to: <code>{createUploadPath()}</code>
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <input
          accept="*/*"
          style={{ display: 'none' }}
          id="hierarchical-file-upload"
          multiple
          type="file"
          onChange={onFileUpload}
        />
        <label htmlFor="hierarchical-file-upload">
          <Button 
            variant="contained" 
            component="span" 
            startIcon={<UploadIcon />}
            disabled={!selectedHierarchy.project}
          >
            Upload Files Here
          </Button>
        </label>
      </DialogActions>
    </Dialog>
  );
};

// FIXED: New Label Creation Dialog
export const NewLabelDialog = ({
  open,
  onClose,
  newLabel,
  onChange, // Changed from onLabelChange to onChange
  onCreate, // Changed from onCreateLabel to onCreate
  error,    // Changed from labelError to error
  isLoading // Changed from isLabelLoading to isLoading
}) => {
  
  // Handle input changes properly
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    
    if (onChange) {
      // Create a proper event object
      const syntheticEvent = {
        target: {
          name,
          value
        }
      };
      onChange(syntheticEvent);
    }
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    if (onCreate && newLabel?.name?.trim()) {
      onCreate();
    }
  };


 
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
    >
      <DialogTitle>Create New Label</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="label-name"
          name="name"
          label="Label Name"
          type="text"
          fullWidth
          variant="outlined"
          value={newLabel?.name || ''}
          onChange={handleInputChange}
          error={!!error}
          helperText={error || 'Enter a unique name for this label'}
          disabled={isLoading}
          sx={{ mb: 3, mt: 1 }}
          required
        />
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="label-color-label">Label Color</InputLabel>
          <Select
            labelId="label-color-label"
            id="label-color"
            name="color" 
            value={newLabel?.color || '#0052cc'}
            label="Label Color"
            onChange={handleInputChange}
            disabled={isLoading}
          >
            {labelColors.map((color) => (
              <MenuItem key={color.value} value={color.value}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      width: 20, 
                      height: 20, 
                      borderRadius: '50%', 
                      backgroundColor: color.value,
                      mr: 1
                    }} 
                  />
                  <Typography>{color.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
       
        {/* Preview */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 2 }}>Preview:</Typography>
          {newLabel?.name ? (
            <Chip
              label={newLabel.name}
              style={{ 
                backgroundColor: newLabel.color || '#0052cc',
                color: 'white'
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Enter a label name to see preview
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          variant="contained" 
          disabled={isLoading || !newLabel?.name?.trim()}
          startIcon={isLoading ? null : <AddIcon />}
        >
          {isLoading ? 'Creating...' : 'Create Label'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};